import React, { Component } from 'react';
import moment from 'moment';
import './TripPanel.css';
import TripPanelFilter from './TripPanelFilter';
import TripPanelTable from './TripPanelTable';
import { api } from '../../../../../services';
import { Spin } from 'antd';
import { inject, observer } from 'mobx-react';
import { Parser as JSON2CSVParser } from 'json2csv';
import { utils } from '../../../../../helpers';
import { Map as MyMap } from '../../../../../classes';

@inject('translationsStore', 'commonStore')
@observer
export default class TripPanel extends Component {
  constructor(props) {
    super(props);

    this.state = {
      summary: {},
      journeys: [],
      loading: false,
      startDate: moment()
        .subtract(7, 'days')
        .startOf('day')
        .toDate(),
      endDate: moment()
        .add(1, 'day')
        .startOf('day')
        .toDate()
    };

    this.MapRef = React.createRef();
  }

  componentDidMount = () => {
    if (this.props.car) {
      this.setState({ carId: this.props.car.id }, () => {
        this.loadTrips();
      });
    }
  };

  componentDidUpdate(prevProps) {
    if (this.props.car && this.props.car.id) {
      if (!prevProps.car || prevProps.car.id !== this.props.car.id) {
        this.setState({ carId: this.props.car.id }, () => {
          this.loadTrips();
        });
      }
    }
  }

  loadTrips() {
    const { carId } = this.state;
    if (carId) {
      this.setState({ loading: true });
      const startDate = moment(this.state.startDate).format('YYYY-MM-DD');
      const endDate = moment(this.state.endDate).format('YYYY-MM-DD');
      api(`get journeys`, carId, startDate, endDate).then(({ journeys, summary }) => {
        this.setState({ loading: false, journeys: journeys.map((journey, i) => ({ id: i, ...journey })), summary });
      });
    }
  }

  filterChanged = (startDate, endDate) => {
    this.setState({ startDate, endDate }, () => {
      this.loadTrips();
    });
  };

  mapAddressFromReverseGeocode = (mapInstance, { lat = 0, lng = 0, notAvailable = '' } = {}) => {
    return new Promise(resolve => {
      let place = notAvailable;
      mapInstance
        .getAddressFromReverseGeocode({ lat, lng })
        .then(address => (place = address.label))
        .finally(() => {
          resolve(place);
        });
    });
  };

  downloadTrips = () => {
    const { translationsStore, car } = this.props;
    const { journeys, startDate, endDate, carId } = this.state;
    const notAvailablePosition = translationsStore.translate('tripsPanel.export.notAvailablePosition');
    const notAvailableDate = translationsStore.translate('tripsPanel.export.notAvailableDate');
    const cMap = new MyMap('dummyMap');

    if (journeys.length > 0) {
      const fields = [
        {
          label: translationsStore.translate('tripsPanel.export.columns.startTime'),
          value: row => (row.startAt ? new Date(row.startAt).toLocaleString() : notAvailableDate)
        },
        {
          label: translationsStore.translate('tripsPanel.export.columns.endTime'),
          value: row => (row.endAt ? new Date(row.endAt).toLocaleString() : notAvailableDate)
        },
        {
          label: translationsStore.translate('tripsPanel.export.columns.startPlace'),
          value: row => row.startPlace
        },
        {
          label: translationsStore.translate('tripsPanel.export.columns.endPlace'),
          value: row => row.endPlace
        },
        {
          label: translationsStore.translate('tripsPanel.export.columns.distance'),
          value: row => row.distance
        },
        {
          label: translationsStore.translate('tripsPanel.export.columns.duration'),
          value: row => moment.duration(row.duration || 0, 'seconds').format('h:mm:ss', { trim: false })
        },
        {
          label: translationsStore.translate('tripsPanel.export.columns.rating'),
          value: row => row.rating
        },
        {
          label: translationsStore.translate('tripsPanel.export.columns.brakes'),
          value: row => row.brakes || 0
        },
        {
          label: translationsStore.translate('tripsPanel.export.columns.accelerations'),
          value: row => row.accelerations || 0
        },
        {
          label: translationsStore.translate('tripsPanel.export.columns.overDrives'),
          value: row => row.overDrives || 0
        },
        {
          label: translationsStore.translate('tripsPanel.export.columns.dayTime'),
          value: row => row.dayDrivePercentage
        },
        {
          label: translationsStore.translate('tripsPanel.export.columns.nightTime'),
          value: row => 100 - row.dayDrivePercentage
        }
      ];
      const exportFilename = `trips_${moment(startDate).format('YYYYMMDD')}_${moment(endDate).format('YYYYMMDD')}_${car.plate}.csv`;

      const detailedCalls = journeys.map(journey => api('get one day journeys', carId, journey.journeyDate));
      Promise.all(detailedCalls).then(fulfilled => {
        let rawJourneys = fulfilled
          .reduce((acc, daily) => {
            acc.push(...daily.journeys);
            return acc;
          }, [])
          .sort((j1, j2) => utils.sortDate(j2.startAt, j1.startAt));

        // determinazione dettagliata indirizzo da posizione
        const detailedPromises = rawJourneys.map(item => {
          const startPos = item.positions.slice(0, 1).pop();
          const startLat = startPos ? startPos.loc[1] : null;
          const startLng = startPos ? startPos.loc[0] : null;
          const endPos = item.positions.slice(-1).pop();
          const endLat = endPos ? endPos.loc[1] : null;
          const endLng = endPos ? endPos.loc[0] : null;

          const itemPromises = [
            startPos ? this.mapAddressFromReverseGeocode(cMap, { lat: startLat, lng: startLng, notAvailablePosition }) : Promise.resolve(notAvailablePosition),
            endPos ? this.mapAddressFromReverseGeocode(cMap, { lat: endLat, lng: endLng, notAvailablePosition }) : Promise.resolve(notAvailablePosition)
          ];

          return new Promise(resolve => {
            Promise.all(itemPromises).then(fulfilled => resolve({ ...item, startPlace: fulfilled[0], endPlace: fulfilled[1] }));
          });
        });

        Promise.all(detailedPromises).then(detailedJourneys => {
          try {
            const parser = new JSON2CSVParser({ fields, delimiter: ';', withBOM: true });
            const csv = parser.parse(detailedJourneys);
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');

            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', exportFilename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          } catch (err) {
            console.error(err);
          }
        });
      });
    }
  };

  render() {
    const { translationsStore, commonStore } = this.props;
    const { plate, serviceLevel } = this.props.car || {};
    const { carId, loading, journeys, startDate, endDate, summary } = this.state;
    return (
      <Spin tip={translationsStore.translate('tripsPanel.loading')} spinning={loading && !commonStore.loading} size="large">
        <div className="TripPanel">
          <TripPanelFilter startDate={startDate} endDate={endDate} onChange={this.filterChanged} summary={summary} onDownloadTrips={this.downloadTrips} />
          <TripPanelTable carId={carId} plate={plate} journeys={journeys} serviceLevel={serviceLevel}/>
        </div>
        <div id="dummyMap" style={{ display: 'none' }} />
      </Spin>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/App/ClientListWithMap/ClientDetail/TripPanel/TripPanel.js